import { FileText, Receipt, Download, ExternalLink, FolderOpen } from "lucide-react";
import Badge from "../components/Badge";
import LoadingSkeleton from "../components/LoadingSkeleton";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "../lib/useQuery";
import { getDocuments } from "../lib/queries";

const DOC_CONFIG = {
  contrato: {
    icon: FileText,
    bg: "bg-blue-50 text-blue-500",
    label: "Contrato",
  },
  factura: {
    icon: Receipt,
    bg: "bg-emerald-50 text-emerald-500",
    label: "Factura",
  },
};

function DocItem({ doc }) {
  const config = DOC_CONFIG[doc.doc_type] || DOC_CONFIG.contrato;
  const Icon = config.icon;
  const hasUrl = !!doc.file_url;

  return (
    <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${config.bg}`}>
        <Icon size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 truncate">{doc.name}</h4>
        {doc.date && (
          <p className="text-xs text-gray-400 mt-0.5">
            {new Date(doc.date).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}
          </p>
        )}
      </div>
      <Badge variant={hasUrl ? "success" : "warning"}>
        {hasUrl ? "Disponible" : "Pendiente"}
      </Badge>
      {hasUrl && (
        <a
          href={doc.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-300 transition-all"
        >
          <ExternalLink size={16} />
        </a>
      )}
    </div>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const { data: documents, loading } = useQuery(() => getDocuments(user.id), [user.id]);

  if (loading) return <LoadingSkeleton />;

  const docs = documents ?? [];

  return (
    <div className="animate-fade-in">
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <FolderOpen size={18} className="text-brand" />
            Mis Documentos
          </h3>
        </div>
        <div className="p-4 md:p-6">
          {docs.length > 0 ? (
            <div className="space-y-3">
              {docs.map((doc) => (
                <DocItem key={doc.id} doc={doc} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FolderOpen size={32} className="text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No hay documentos disponibles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
